import { formatDateBR, formatName } from '@/lib/format';
import { parseOnusItems } from './onus';
import type { ClienteDetail, Documento } from './schemas';

export type CheckStatus = 'ok' | 'pendente' | 'vencido';

export interface CheckRow {
  label: string;
  status: CheckStatus;
  detail: string;
}

export interface SecaoImovel {
  key: string;
  tipo: 'Próprio' | 'Arrendado' | 'Comodato' | 'Parceria';
  areaConjunta?: number | null;
  vinculoPendente?: boolean;
  imovel: Record<string, unknown>;
  denominacao: string;
  matricula: string | null;
  area: number | null;
}

export const TIPO_DOC_LABEL: Record<string, string> = {
  CCIR: 'CCIR',
  ccir: 'CCIR',
  ITR: 'ITR',
  itr: 'ITR',
  certidao_inteiro_teor: 'Certidão de Inteiro Teor',
  CAR: 'CAR',
  car: 'CAR',
  dap_caf: 'DAP/CAF',
  memorial_descritivo: 'Memorial descritivo',
  arrendamento: 'Contrato de Arrendamento',
  licenca_ambiental: 'Licença ambiental',
  proposta_credito: 'Proposta de crédito',
  rg: 'RG',
  cnd_nirf: 'CND NIRF',
  outros: 'Outros',
};

export function tipoDocLabel(t: string | null | undefined): string {
  if (!t) return '—';
  return TIPO_DOC_LABEL[t] ?? t.replace(/_/g, ' ');
}

export function diasAte(d: string): number {
  const hoje = new Date();
  hoje.setHours(0, 0, 0, 0);
  const alvo = new Date(d.length >= 10 ? d.slice(0, 10) + 'T00:00:00' : d);
  return Math.floor((alvo.getTime() - hoje.getTime()) / 86400000);
}

export function statusTone(s: CheckStatus): 'ok' | 'warn' | 'danger' {
  if (s === 'ok') return 'ok';
  if (s === 'vencido') return 'danger';
  return 'warn';
}

export function statusLabel(s: CheckStatus): string {
  if (s === 'ok') return 'OK';
  if (s === 'vencido') return 'Vencido';
  return 'Pendente';
}

export function docSituacao(d: Documento): {
  tone: 'ok' | 'warn' | 'danger' | 'neutral';
  label: string;
} {
  if (!d.validade) return { tone: 'neutral', label: 'Sem validade' };
  const dias = diasAte(d.validade);
  if (dias < 0) return { tone: 'danger', label: `Vencido em ${formatDateBR(d.validade)}` };
  if (dias <= 30) return { tone: 'warn', label: `Vence em ${dias}d` };
  return { tone: 'ok', label: `Válido até ${formatDateBR(d.validade)}` };
}

export function buildChecks(
  imovel: Record<string, unknown>,
  docs: Documento[],
): CheckRow[] {
  const rows: CheckRow[] = [];

  if (docs.length === 0) {
    rows.push({
      label: 'Documentos relacionados',
      status: 'pendente',
      detail: 'Nenhum vinculado',
    });
  } else {
    const venc = docs.filter((d) => d.validade && diasAte(d.validade) < 0).length;
    const total = `${docs.length} ${docs.length === 1 ? 'documento' : 'documentos'}`;
    rows.push({
      label: 'Documentos relacionados',
      status: venc > 0 ? 'vencido' : 'ok',
      detail: venc > 0 ? `${total}, ${venc} vencido${venc === 1 ? '' : 's'}` : total,
    });
  }

  const matricula = imovel.matricula as string | null | undefined;
  const cartorio = imovel.cartorio as string | null | undefined;
  const validadeCert = imovel.validade_certidao as string | null | undefined;
  if (!matricula) {
    rows.push({ label: 'Certidão de Inteiro Teor', status: 'pendente', detail: 'Sem matrícula' });
  } else {
    const dias = validadeCert ? diasAte(validadeCert) : null;
    const status: CheckStatus = dias !== null && dias < 0 ? 'vencido' : 'ok';
    const parts = [`Matrícula ${matricula}`];
    if (cartorio) parts.push(formatName(cartorio));
    if (validadeCert) parts.push(`válida até ${formatDateBR(validadeCert)}`);
    rows.push({ label: 'Certidão de Inteiro Teor', status, detail: parts.join(', ') });
  }

  const carCodigo = imovel.car_codigo as string | null | undefined;
  const carValidade = imovel.car_validade as string | null | undefined;
  if (!carCodigo) {
    rows.push({ label: 'CAR', status: 'pendente', detail: 'Sem código' });
  } else {
    const dias = carValidade ? diasAte(carValidade) : null;
    rows.push({
      label: 'CAR',
      status: dias !== null && dias < 0 ? 'vencido' : 'ok',
      detail: `${carCodigo}${carValidade ? `, válido até ${formatDateBR(carValidade)}` : ''}`,
    });
  }

  const ccirStatus = imovel.ccir_status as string | null | undefined;
  const ccirAno = imovel.ccir_ano as number | string | null | undefined;
  const anoAtual = new Date().getFullYear();
  if (!ccirStatus && !ccirAno) {
    rows.push({ label: 'CCIR', status: 'pendente', detail: 'Sem cadastro' });
  } else {
    const ano = ccirAno != null ? Number(ccirAno) : null;
    const venceu = ano !== null && ano < anoAtual - 1;
    rows.push({
      label: 'CCIR',
      status: venceu ? 'vencido' : 'ok',
      detail: [ccirStatus, ano ? String(ano) : null].filter(Boolean).join(', ') || '—',
    });
  }

  const itrStatus = imovel.itr_status as string | null | undefined;
  const itrAno = imovel.itr_ano as number | string | null | undefined;
  if (!itrStatus) {
    rows.push({ label: 'ITR / CND', status: 'pendente', detail: 'Sem cadastro' });
  } else {
    const ano = itrAno != null ? Number(itrAno) : null;
    const venceu = ano !== null && ano < anoAtual - 1;
    rows.push({
      label: 'ITR / CND',
      status: venceu ? 'vencido' : 'ok',
      detail: `${itrStatus}${ano ? `, ${ano}` : ''}`,
    });
  }

  const onus = parseOnusItems(imovel.onus_items);
  if (onus.length === 0) {
    rows.push({ label: 'Ônus e Gravames', status: 'ok', detail: 'Livre' });
  } else {
    const venc = onus.filter((o) => o.status === 'vencido_sem_baixa').length;
    const vig = onus.filter((o) => o.status === 'vigente').length;
    const partes = [`${onus.length} ${onus.length === 1 ? 'registro' : 'registros'}`];
    if (vig > 0) partes.push(`${vig} ${vig === 1 ? 'vigente' : 'vigentes'}`);
    if (venc > 0) partes.push(`${venc} pendente${venc === 1 ? '' : 's'} de baixa`);
    rows.push({
      label: 'Ônus e Gravames',
      status: venc > 0 ? 'vencido' : vig > 0 ? 'pendente' : 'ok',
      detail: partes.join(', '),
    });
  }

  return rows;
}

export function calcScore(checks: CheckRow[]): number {
  const ok = checks.filter((c) => c.status === 'ok').length;
  return Math.round((ok / checks.length) * 100);
}

export function buildSecoes(cliente: ClienteDetail): SecaoImovel[] {
  const out: SecaoImovel[] = [];
  for (const im of cliente.imoveis) {
    const data = im as unknown as Record<string, unknown>;
    out.push({
      key: `proprio-${im.id}`,
      tipo: 'Próprio',
      imovel: data,
      denominacao: im.denominacao ? formatName(im.denominacao) : `Imóvel ${im.id}`,
      matricula: im.matricula ?? null,
      area: im.area_total_ha ?? null,
    });
  }
  for (const arr of cliente.arrendamentos) {
    if (['rescindido','vencido'].includes(arr.status ?? '')) continue;
    const tipoTexto=String(arr.contrato_tipo ?? '').toLowerCase();
    const tipo = tipoTexto.includes('comodato') ? 'Comodato' : tipoTexto.includes('parceria') ? 'Parceria' : 'Arrendado';
    const linked = arr.imoveis?.length ? arr.imoveis : arr.imovel ? [arr.imovel] : [];
    const imoveis = [...new Map(linked.map(im=>[String(im.id),im])).values()];
    if (!imoveis.length) {
      const objeto=String(arr.contrato_objeto ?? arr.imovel_denominacao ?? `Contrato ${arr.id}`);
      const mat=arr.imovel_matricula ?? objeto.match(/matr[ií]cula\s*(?:n[ºo.]?\s*)?([\d.]+)/i)?.[1] ?? null;
      out.push({key:`arrendado-${arr.id}`, tipo, imovel:{observacoes:arr.observacoes, contrato_objeto:objeto}, denominacao:objeto, matricula:mat, area:arr.area_ha ?? null, vinculoPendente:true});
      continue;
    }
    for (const im of imoveis) {
      out.push({key:`arrendado-${arr.id}-${im.id}`, tipo, imovel:im,
        denominacao:formatName(String(im.denominacao ?? arr.imovel_denominacao ?? `Imóvel ${im.id}`)),
        matricula:(im.matricula as string|null) ?? arr.imovel_matricula ?? null,
        area:imoveis.length>1 ? null : arr.area_ha ?? null,
        areaConjunta:imoveis.length>1 ? arr.area_ha ?? null : null});
    }
  }
  return out;
}

export function docsDoImovel(docs: Documento[], secao: SecaoImovel): Documento[] {
  return docs.filter(
    (d) =>
      (secao.imovel.id != null && d.imovel_id != null && String(d.imovel_id) === String(secao.imovel.id)) ||
      (secao.matricula != null &&
        String((d as Record<string, unknown>).imovel_matricula ?? '') === secao.matricula),
  );
}

export type Classificacao = 'Apto' | 'Apto c/ Ressalva' | 'Pendente';

export function classificacao(
  score: number,
  pendCount: number,
): { label: Classificacao; tone: 'ok' | 'warn' | 'danger' } {
  if (pendCount === 0) return { label: 'Apto', tone: 'ok' };
  if (score >= 60) return { label: 'Apto c/ Ressalva', tone: 'warn' };
  return { label: 'Pendente', tone: 'danger' };
}

// Detecta imóveis em duplicidade por matrícula e código CAR.
// Match case-insensitive, ignora espaços e pontuação para CAR (códigos vêm
// com pontos em alguns lugares e sem em outros).
export interface Duplicidade {
  motivos: ('Matrícula' | 'CAR')[];
  irmaos: string[]; // denominacao dos outros imóveis no mesmo grupo
}

function normCar(s: string): string {
  return s.replace(/[.\s-]/g, '').toLowerCase();
}

function normMat(s: string): string {
  return s.replace(/\s+/g, '').toLowerCase();
}

export function detectarDuplicidades(secoes: SecaoImovel[]): Map<string, Duplicidade> {
  const porMatricula = new Map<string, SecaoImovel[]>();
  const porCar = new Map<string, SecaoImovel[]>();

  for (const s of secoes) {
    if (s.matricula && s.matricula.trim()) {
      const k = normMat(s.matricula);
      if (!porMatricula.has(k)) porMatricula.set(k, []);
      porMatricula.get(k)!.push(s);
    }
    const car = (s.imovel.car_codigo as string | null | undefined)?.trim();
    if (car) {
      const k = normCar(car);
      if (!porCar.has(k)) porCar.set(k, []);
      porCar.get(k)!.push(s);
    }
  }

  const out = new Map<string, Duplicidade>();
  function record(items: SecaoImovel[], motivo: 'Matrícula' | 'CAR') {
    if (items.length < 2) return;
    for (const s of items) {
      const irmaos = items.filter((i) => i.key !== s.key).map((i) => i.denominacao);
      const prev = out.get(s.key);
      if (prev) {
        if (!prev.motivos.includes(motivo)) prev.motivos.push(motivo);
        for (const ir of irmaos) if (!prev.irmaos.includes(ir)) prev.irmaos.push(ir);
      } else {
        out.set(s.key, { motivos: [motivo], irmaos: [...irmaos] });
      }
    }
  }
  for (const items of porMatricula.values()) record(items, 'Matrícula');
  for (const items of porCar.values()) record(items, 'CAR');
  return out;
}
